// Reads General settings on every render and applies side-effects to the DOM
// so the rest of the app picks them up via CSS variables + body classes.
//
// Mount this once near the app root (App.tsx) — it returns null.

import { useEffect } from 'react';
import { useSettings } from '../hooks/useSettings.js';
import { useSession } from '../hooks/useSession.js';

export function SettingsEffects() {
  const s = useSettings();
  const { session } = useSession();
  const orgThemeColor = session?.activeOrg?.themeColor ?? null;

  // Dark mode — sets data-theme on <html>, which CSS hooks via [data-theme=dark]
  useEffect(() => {
    const root = document.documentElement;
    let resolved: 'light' | 'dark';
    if (s.darkMode === 'system') {
      resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      resolved = s.darkMode;
    }
    root.setAttribute('data-theme', resolved);

    // Track system preference changes so 'system' mode reacts live.
    if (s.darkMode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = (e: MediaQueryListEvent) => {
        root.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      };
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    return undefined;
  }, [s.darkMode]);

  // Slim sidebar — adds class to <body>; shell layout adjusts via CSS.
  useEffect(() => {
    document.body.classList.toggle('slim-sidebar', !!s.slimSidebar);
  }, [s.slimSidebar]);

  // Theme color — overrides the accent-primary CSS variable globally.
  // CTSK-283: the source is the ACTIVE ORG's themeColor (per-org
  // attribute), not the app-wide General envelope. NULL/absent means
  // "use the :root default" — clear any inline override so the
  // stylesheet value (#2F6B4E) shows. Org switch is a full page
  // reload (OrgSwitcher), so this boot-time application also covers
  // repainting after a switch.
  useEffect(() => {
    applyAccentColor(orgThemeColor);
  }, [orgThemeColor]);

  // Invert thumbnails — body class for any image rendering CSS to hook.
  useEffect(() => {
    document.body.classList.toggle('invert-thumbnails', !!s.invertThumbnails);
  }, [s.invertThumbnails]);

  return null;
}

/** Apply (or clear) the accent CSS variables. Exported so the org
 *  theme picker (Settings → General) can repaint immediately on a
 *  successful save without waiting for a session refetch. Passing
 *  null/invalid clears the inline override back to the :root default. */
export function applyAccentColor(color: string | null | undefined): void {
  const root = document.documentElement;
  if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
    root.style.setProperty('--accent-primary', color);
    // Derive a hover shade by darkening ~12%.
    root.style.setProperty('--accent-primary-hover', darken(color, 0.12));
  } else {
    root.style.removeProperty('--accent-primary');
    root.style.removeProperty('--accent-primary-hover');
  }
}

function darken(hex: string, amount: number): string {
  const m = hex.match(/^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (!m) return hex;
  const r = Math.max(0, Math.round(parseInt(m[1]!, 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(m[2]!, 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(m[3]!, 16) * (1 - amount)));
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}
